<div>
  <div class="flex items-center gap-3 mb-4">
    <div class="flex-none w-80">
      <span class="relative block">
        <i class="pi pi-search absolute top-1/2 left-3 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
        <input
          pInputText
          type="text"
          class="w-full pl-9 pr-9"
          [placeholder]="'animalNotes.search.placeholder' | transloco"
          [(ngModel)]="searchTerm"
        />
        @if (searchTerm) {
          <i class="pi pi-times absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600" (click)="searchTerm = ''"></i>
        }
      </span>
    </div>

    <div class="flex-1 min-w-0">
      @if (selectedNoteIds.length) {
        <div class="flex items-center gap-3 py-0.5 px-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span class="text-sm font-semibold text-blue-700 whitespace-nowrap">
            {{ 'animalNotes.selection.count' | transloco:{count: selectedNoteIds.length} }}
          </span>
          <p-button
            severity="secondary"
            size="small"
            [text]="true"
            icon="pi pi-times"
            [label]="'animalNotes.selection.clear' | transloco"
            (onClick)="clearSelection()"
          />
        </div>
      }
    </div>

    <div class="flex-none flex items-center gap-2">
      <p-button
        icon="pi pi-plus"
        (onClick)="openCreateNoteDialog()"
      />

      <p-button
        icon="pi pi-file-pdf"
        [label]="('animalNotes.actions.exportSelected' | transloco) + (selectedNoteIds.length ? ' (' + selectedNoteIds.length + ')' : '')"
        [disabled]="!canExportSelected()"
        (onClick)="openBulkExportDialog()"
      />
    </div>
  </div>

  @if (loading) {
    <div class="text-primary-500 text-bold p-4">{{ 'animalNotes.loading' | transloco }}</div>
  } @else if (!notes.length) {
    <p-message severity="info">{{ 'animalNotes.empty' | transloco }}</p-message>
  } @else if (!activeNotes.length) {
    @if (searchTerm) {
      <p-message severity="info">{{ 'animalNotes.emptyFiltered' | transloco }}</p-message>
    } @else {
      <p-message severity="info">{{ 'animalNotes.emptyActive' | transloco }}</p-message>
    }
  } @else {
    <div class="flex items-center gap-2 px-1 pb-2">
      <p-checkbox
        [binary]="true"
        [disabled]="!selectableNoteIds.length"
        [ngModel]="allSelectableSelected"
        (ngModelChange)="toggleSelectAll($event)"
      />
      <span class="text-sm text-gray-600">{{ 'animalNotes.selection.selectAll' | transloco }}</span>
    </div>

    <div class="grid gap-3 items-start grid-cols-[repeat(auto-fill,minmax(22rem,1fr))]">
      @for (note of activeNotes; track note.id) {
        <p-panel
          styleClass="relative h-full min-h-48 p-3 pb-8 bg-[var(--p-primary-50)] border border-gray-200 rounded-lg transition-colors"
          [pt]="{ header: { style: 'padding: 0' } }"
        >
          <ng-template pTemplate="header">
            <div class="flex flex-wrap items-center justify-between w-full gap-2">
              <div class="flex items-center gap-2">
                <p-checkbox
                  [binary]="true"
                  size="large"
                  [ngModel]="isSelected(note.id)"
                  (ngModelChange)="toggleNoteSelection(note.id, $event)"
                />
                <div class="min-w-0">
                  @if (note.title) {
                    <div class="font-bold text-base break-words">{{ note.title }}</div>
                  }
                  <div class="font-semibold text-sm">
                    {{ 'animalNotes.meta.by' | transloco }}
                    {{ (note.creator?.displayName) || '-' }}
                    <br/>
                    {{ 'animalNotes.meta.on' | transloco }}
                    {{ note.createdAt | date:'dd.MM.yyyy HH:mm' }} Uhr
                  </div>
                </div>
              </div>
              @if (editNoteId !== note.id) {
                <div class="flex flex-wrap justify-end gap-2">
                  <p-button
                    severity="secondary"
                    size="small"
                    icon="pi pi-clone"
                    [pTooltip]="'animalNotes.actions.copyHint' | transloco"
                    tooltipPosition="top"
                    [disabled]="saving"
                    (onClick)="copyNote(note)"
                  />

                  <p-button
                    severity="secondary"
                    size="small"
                    icon="pi pi-file-edit"
                    [pTooltip]="'animalNotes.actions.editHint' | transloco"
                    tooltipPosition="top"
                    [disabled]="saving"
                    (onClick)="startEdit(note)"
                  />

                  <p-button
                    severity="danger"
                    size="small"
                    icon="pi pi-trash"
                    [pTooltip]="'animalNotes.actions.deleteHint' | transloco"
                    tooltipPosition="top"
                    [disabled]="saving"
                    (onClick)="openDeleteDialog(note)"
                  />
                </div>
              }
            </div>
          </ng-template>

          @if (editNoteId === note.id) {
            <div class="mb-3">
              <p-iftalabel>
                <input
                  pInputText
                  type="text"
                  class="w-full"
                  id="editNoteTitle"
                  [(ngModel)]="editTitle"
                  [maxlength]="255"
                />
                <label for="editNoteTitle">{{ 'animalNotes.title.label' | transloco }}</label>
              </p-iftalabel>
            </div>
            <app-editor-field
              [(ngModel)]="editDescription"
              id="editNoteDescription"
            />
            <div class="flex justify-end gap-2 mt-3">
              <p-button
                severity="secondary"
                size="small"
                icon="pi pi-times"
                [label]="'modal.cancel' | transloco"
                (onClick)="cancelEdit()"
              />
              <p-button
                size="small"
                icon="pi pi-save"
                [label]="'modal.save' | transloco"
                [disabled]="saving || editNoteEmpty"
                (onClick)="saveEdit(note)"
              />
            </div>
          } @else {
              <div
                class="max-h-48 overflow-y-auto break-words [&_p]:mb-2 [&_ul]:mb-2 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:mb-2 [&_ol]:pl-6 [&_ol]:list-decimal [&_a]:text-blue-600 [&_a]:underline [&_.ql-align-center]:text-center [&_.ql-align-right]:text-right [&_.ql-align-justify]:text-justify"
                [innerHTML]="note.description"
              ></div>
              <div class="absolute bottom-0 text-xs text-gray-500 mb-3">
                {{ 'animalNotes.meta.updatedAt' | transloco }}: {{ note.updatedAt | date:'dd.MM.yyyy HH:mm' }}
              </div>
          }
        </p-panel>
      }
    </div>
  }

  @if (!loadingExports && exports.length) {
    <div class="mt-6">
      <h4 class="mb-3">{{ 'animalNotes.history.headline' | transloco }}</h4>

      <div class="grid gap-3 items-start grid-cols-[repeat(auto-fill,minmax(24rem,1fr))]">
        @for (exportEntry of exports; track exportEntry.id) {
          <p-panel [toggleable]="true" [collapsed]="false">
            <ng-template pTemplate="header">
              <div class="flex items-center gap-2.5 w-full">
                <i class="pi pi-file-pdf text-red-700 text-xl"></i>
                <div class="flex flex-1 flex-col gap-1">
                  <div class="font-semibold">
                    {{ exportEntry.signedAt | date:'dd.MM.yyyy HH:mm' }} {{ 'animalNotes.history.clock' | transloco }}
                  </div>
                  <div class="text-sm text-gray-600">
                    {{ (exportEntry.user?.displayName) || '-' }}
                    · {{ 'animalNotes.history.noteCount' | transloco:{count: notesForExport(exportEntry).length} }}
                  </div>
                </div>
                <p-button
                  severity="primary"
                  size="small"
                  icon="pi pi-eye"
                  (onClick)="openExportedPdf(exportEntry)"
                />
              </div>
            </ng-template>

            <div class="flex flex-col gap-2.5">
              @if (notesForExport(exportEntry).length) {
                @for (note of notesForExport(exportEntry); track note.id) {
                  <div class="py-2.5 px-3 bg-gray-50 border border-gray-200 rounded-lg break-words">
                    @if (note.title) {
                      <div class="font-semibold mb-1">{{ note.title }}</div>
                    }
                    <div class="text-xs text-gray-500 mb-1">
                      {{ 'animalNotes.meta.by' | transloco }}
                      {{ (note.creator?.displayName) || '-' }}
                      {{ 'animalNotes.meta.on' | transloco }}
                      {{ note.createdAt | date:'dd.MM.yyyy HH:mm' }}
                    </div>
                    <div
                      class="break-words [&_p]:mb-2 [&_ul]:mb-2 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:mb-2 [&_ol]:pl-6 [&_ol]:list-decimal [&_a]:text-blue-600 [&_a]:underline [&_.ql-align-center]:text-center [&_.ql-align-right]:text-right [&_.ql-align-justify]:text-justify"
                      [innerHTML]="note.description"
                    ></div>
                  </div>
                }
              } @else {
                <div class="text-sm text-gray-500">{{ 'animalNotes.history.notesUnavailable' | transloco }}</div>
              }
            </div>
          </p-panel>
        }
      </div>
    </div>
  }
</div>

<p-dialog
  [header]="'animalNotes.exportDialog.headline' | transloco"
  [(visible)]="exportDialogVisible"
  [style]="{ width: '95vw', maxWidth: '95vw', height: '95vh', maxHeight: '95vh' }"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  (onShow)="onExportDialogShow()"
  (onHide)="closeExportDialog()"
>
  <div class="grid grid-cols-12 gap-4">
    <div class="col-span-12">
      <p-message severity="warn" styleClass="w-full">
        {{ 'animalNotes.exportDialog.lockWarning' | transloco:{count: exportNoteIds.length} }}
      </p-message>
    </div>

    <div class="col-span-12 md:col-span-4 flex flex-col gap-4">
      <p-iftalabel>
        <p-datePicker
          inputId="animalNoteExportDate"
          [(ngModel)]="exportSignedAt"
          [showIcon]="true"
          [iconDisplay]="'input'"
          dateFormat="dd.mm.yy"
        ></p-datePicker>
        <label for="animalNoteExportDate">{{ 'animalNotes.exportDialog.date' | transloco }}</label>
      </p-iftalabel>
      <div class="col-span-12">
        <label class="flex items-start gap-2 cursor-pointer">
          <p-checkbox [binary]="true" [(ngModel)]="exportLockAcknowledged" inputId="exportLockAck" />
          <span class="text-sm">{{ 'animalNotes.exportDialog.acknowledge' | transloco }}</span>
        </label>
      </div>
      <div>
        <h4 class="mb-2">{{ 'animalNotes.exportDialog.signature' | transloco }}</h4>
        @if (renderSignaturePad) {
          <app-signature-pad
            (signatureSaved)="exportSignatureDataUrl = $event"
            (signatureCleared)="exportSignatureDataUrl = null"
          />
        }
      </div>
    </div>

    <div class="col-span-12 md:col-span-8">
      <h4 class="mb-2">{{ 'animalNotes.exportDialog.selectedNotes' | transloco }}</h4>
      <div class="rounded border border-gray-200 p-3 bg-gray-50 h-full">
        <div class="text-sm text-gray-600">
          @if (exportNotes.length) {
            @for (note of exportNotes; track note.id) {
              <div>
                @if (note.title) {
                  <span class="font-semibold">{{ note.title }}</span> —
                }
                {{ toPlainText(note.description) }}
              </div>
            }
          } @else {
            {{ 'animalNotes.exportDialog.noSelection' | transloco }}
          }
        </div>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <p-button
        severity="primary"
        icon="pi pi-times"
        [label]="'modal.cancel' | transloco"
        [disabled]="finalizing"
        (onClick)="closeExportDialog()"
      />
      <p-button
        icon="pi pi-save"
        [label]="'animalNotes.exportDialog.confirm' | transloco"
        [loading]="finalizing"
        [disabled]="!canConfirmExport()"
        (onClick)="exportSelectedNotes()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [header]="'animalNotes.new.label' | transloco"
  [(visible)]="createNoteDialogVisible"
  [style]="{ width: '40rem', maxWidth: '95vw' }"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  (onHide)="closeCreateNoteDialog()"
>

  <div class="mb-4 mt-2">
    <p-iftalabel>
      <input
        pInputText
        type="text"
        class="w-full"
        id="newNoteTitle"
        [(ngModel)]="newTitle"
        [maxlength]="255"
      />
      <label for="newNoteTitle">{{ 'animalNotes.title.label' | transloco }}</label>
    </p-iftalabel>
  </div>

  <app-editor-field
    [(ngModel)]="newDescription"
    id="newNoteDescription"
  />

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <p-button
        severity="secondary"
        icon="pi pi-times"
        [label]="'modal.cancel' | transloco"
        [disabled]="saving"
        (onClick)="closeCreateNoteDialog()"
      />
      <p-button
        icon="pi pi-plus"
        [label]="'animalNotes.new.create' | transloco"
        [loading]="saving"
        [disabled]="saving || newNoteEmpty"
        (onClick)="createNote()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [header]="'animalNotes.deleteDialog.headline' | transloco"
  [(visible)]="deleteDialogVisible"
  [style]="{ width: '32rem', maxWidth: '95vw' }"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  (onHide)="closeDeleteDialog()"
>
  <div class="flex items-start gap-3">
    <i class="pi pi-exclamation-triangle text-2xl text-red-600"></i>
    <div class="text-sm">
      <p class="mb-2">{{ 'animalNotes.deleteDialog.message' | transloco }}</p>
      @if (deleteNote) {
        <div class="py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg break-words text-gray-600">
          @if (deleteNote.title) {
            <div class="font-semibold text-gray-700">{{ deleteNote.title }}</div>
          }
          {{ toPlainText(deleteNote.description) }}
        </div>
      }
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <p-button
        severity="secondary"
        icon="pi pi-times"
        [label]="'modal.cancel' | transloco"
        [disabled]="deleting"
        (onClick)="closeDeleteDialog()"
      />
      <p-button
        severity="danger"
        icon="pi pi-trash"
        [label]="'animalNotes.deleteDialog.confirm' | transloco"
        [loading]="deleting"
        [disabled]="deleting"
        (onClick)="confirmDelete()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [header]="pdfPreviewTitle || ('animalNotes.history.viewPdf' | transloco)"
  [(visible)]="pdfPreviewVisible"
  [style]="{ width: '92vw', maxWidth: '92vw' }"
  [contentStyle]="{ height: '85vh' }"
  [modal]="true"
  [maximizable]="true"
  [draggable]="false"
  [resizable]="false"
  (onHide)="closePdfPreview()"
>
  <div class="h-full min-h-[70vh]">
    @if (pdfPreviewLoading) {
      <div class="flex items-center justify-center h-full min-h-[70vh]">
        <p-progressSpinner strokeWidth="4"></p-progressSpinner>
      </div>
    } @else if (pdfPreviewData) {
      <app-pdf-viewer [pdf]="pdfPreviewData" height="75vh"></app-pdf-viewer>
    } @else {
      <p-message severity="info">{{ 'animalNotes.empty' | transloco }}</p-message>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <p-button
        severity="primary"
        icon="pi pi-download"
        [disabled]="pdfPreviewLoading || !pdfPreviewData"
        (onClick)="downloadPreviewPdf()"
      />
      <p-button
        severity="primary"
        icon="pi pi-print"
        [disabled]="pdfPreviewLoading || !pdfPreviewData"
        (onClick)="printPreviewPdf()"
      />
    </div>
  </ng-template>
</p-dialog>
